import type { CarSpec, CrewModule, StyleAction } from './types.js';

export interface CrewModuleDefinition {
  id: CrewModule;
  name: string;
  description: string;
  tradeoff: string;
}

export const CREW_MODULES: readonly CrewModuleDefinition[] = [
  {
    id: 'balanced',
    name: 'STREET SETUP',
    description: 'The original factory-balanced arcade setup.',
    tradeoff: 'NO TRADE-OFF',
  },
  {
    id: 'grip',
    name: 'APEX KIT',
    description: 'Quicker response and stronger braking for technical roads.',
    tradeoff: '+10% HANDLING · +4% BRAKING · -3% TOP SPEED',
  },
  {
    id: 'nitro',
    name: 'BOOST LAB',
    description: 'More straight-line urgency and richer nitro rewards.',
    tradeoff: '+4% ACCEL · -6% HANDLING',
  },
  {
    id: 'armour',
    name: 'IMPACT CELL',
    description: 'Reinforced bodywork keeps a bad contact from ending the run.',
    tradeoff: '-24% DAMAGE · +9% MASS · -3% ACCEL',
  },
] as const;

const MODULE_IDS = new Set<CrewModule>(CREW_MODULES.map((module) => module.id));

export function normaliseCrewModule(value: unknown): CrewModule {
  return MODULE_IDS.has(value as CrewModule) ? value as CrewModule : 'balanced';
}

/** Returns a race-local spec. The catalogue stays immutable and UI statistics
 * cannot accidentally accumulate the modifier across rematches. */
export function carSpecWithCrewModule(car: CarSpec, moduleValue: unknown): CarSpec {
  const module = normaliseCrewModule(moduleValue);
  if (module === 'balanced') return car;
  if (module === 'grip') {
    return {
      ...car,
      topSpeed: car.topSpeed * 0.97,
      handling: car.handling * 1.1,
      braking: car.braking * 1.04,
    };
  }
  if (module === 'nitro') {
    return {
      ...car,
      acceleration: car.acceleration * 1.04,
      handling: car.handling * 0.94,
    };
  }
  return { ...car, mass: car.mass * 1.09, acceleration: car.acceleration * 0.97 };
}

export function crewDamageScale(moduleValue: unknown): number {
  return normaliseCrewModule(moduleValue) === 'armour' ? 0.76 : 1;
}

export function crewNitroRewardScale(moduleValue: unknown, action: StyleAction): number {
  if (normaliseCrewModule(moduleValue) !== 'nitro') return 1;
  return action === 'PIT CREW' ? 1 : 1.18;
}
